import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import api, { apiErr } from "@/lib/api";
import { formatWebinarTime } from "@/lib/format";
import { toast } from "sonner";
import { Clock, Video, Calendar, User, CheckCircle2, ArrowRight } from "lucide-react";

const AGENDA = [
  "The current landscape & biggest opportunities",
  "A proven step-by-step framework you can apply",
  "Live examples, tools and real workflows",
  "Q&A + an exclusive learning offer for attendees",
];

export default function WebinarLanding() {
  const { topic } = useParams();
  const { timezone, selectTopic } = useApp();
  const navigate = useNavigate();
  const [webinar, setWebinar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notify, setNotify] = useState({ name: "", email: "" });

  useEffect(() => {
    selectTopic(topic);
    setLoading(true);
    api.nextWebinar(topic).then((d) => setWebinar(d.available ? d.webinar : null)).finally(() => setLoading(false));
    api.logEvent("webinar.viewed", { topic });
  }, [topic]);

  const submitNotify = async (e) => {
    e.preventDefault();
    try {
      await api.notifyMe({ ...notify, topic, timezone });
      toast.success("You'll be notified when the next session is scheduled.");
      setNotify({ name: "", email: "" });
    } catch (err) { toast.error(apiErr(err)); }
  };

  const t = webinar ? formatWebinarTime(webinar.start_utc, timezone) : null;

  return (
    <div className="mesh-bg noise relative" data-testid="webinar-landing">
      <div className="max-w-7xl mx-auto px-6 py-16 grid lg:grid-cols-2 gap-12 items-start">
        {/* left: info */}
        <div>
          <div className="asf-overline mb-3">Free 45-minute masterclass</div>
          <h1 className="font-display text-4xl md:text-6xl font-extrabold tracking-tighter leading-[0.95]">
            {webinar?.title || `${topic} Masterclass`}
          </h1>
          <p className="text-lg text-slate-600 mt-5 max-w-lg">{webinar?.tagline}</p>

          {webinar && (
            <div className="flex flex-wrap gap-x-8 gap-y-3 mt-8 text-sm">
              <span className="flex items-center gap-2"><Calendar className="h-4 w-4 text-asf-orange" /> {t.dayLabel}</span>
              <span className="flex items-center gap-2"><Clock className="h-4 w-4 text-asf-orange" /> {t.time} {t.tzShort} (your local time)</span>
              <span className="flex items-center gap-2"><Video className="h-4 w-4 text-asf-orange" /> {webinar.location}</span>
              <span className="flex items-center gap-2"><User className="h-4 w-4 text-asf-orange" /> Host: {webinar.host}</span>
            </div>
          )}

          <div className="mt-10">
            <h3 className="font-display text-xl font-semibold mb-4">What you'll learn</h3>
            <ul className="space-y-3">
              {AGENDA.map((a) => <li key={a} className="flex gap-3 text-slate-600"><CheckCircle2 className="h-5 w-5 text-asf-orange shrink-0 mt-0.5" /> {a}</li>)}
            </ul>
          </div>
        </div>

        {/* right: reserve / notify */}
        <div className="lg:sticky lg:top-24">
          {loading ? (
            <div className="rounded-3xl bg-slate-100 h-72 animate-pulse" />
          ) : webinar ? (
            <div className="relative rounded-3xl p-[1px] bg-gradient-to-br from-asf-orange/60 to-blue-500/30">
              <div className="rounded-3xl bg-asf-surface p-8">
                <div className="asf-overline mb-1">Your next session</div>
                <div className="rounded-2xl bg-asf-app/60 border border-slate-200 p-5 my-5 text-center">
                  <div className="font-display text-5xl font-extrabold">{t.time}</div>
                  <div className="text-sm text-slate-500 mt-1">{t.dayLabel} · {t.tzShort} · your local time</div>
                  <div className="text-xs text-slate-500 mt-1">{webinar.duration_min} minutes · Online</div>
                </div>
                <button data-testid="landing-reserve-btn"
                  onClick={() => navigate(`/webinar/${webinar.instance_id}/register`)}
                  className="btn-primary w-full py-4 inline-flex items-center justify-center gap-2">
                  Reserve My Seat <ArrowRight className="h-5 w-5" />
                </button>
                <p className="text-xs text-slate-500 text-center mt-3">Free · No credit card required</p>
              </div>
            </div>
          ) : (
            <div className="asf-card !translate-y-0 hover:!translate-y-0 p-8">
              <h3 className="font-display text-xl font-semibold">New sessions being scheduled</h3>
              <p className="text-slate-500 mt-2 text-sm">Leave your details and we'll notify you when the next {topic} webinar is live.</p>
              <form onSubmit={submitNotify} className="space-y-3 mt-5">
                <input required placeholder="Full name" data-testid="notify-name" value={notify.name}
                  onChange={(e) => setNotify({ ...notify, name: e.target.value })}
                  className="w-full rounded-xl bg-asf-app border border-slate-200 px-4 py-3 outline-none focus:border-asf-orange" />
                <input required type="email" placeholder="Email" data-testid="notify-email" value={notify.email}
                  onChange={(e) => setNotify({ ...notify, email: e.target.value })}
                  className="w-full rounded-xl bg-asf-app border border-slate-200 px-4 py-3 outline-none focus:border-asf-orange" />
                <button data-testid="notify-submit" className="btn-primary w-full py-3.5">Notify Me</button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
